import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../../firebase/config';

// Helper function to generate shift instances for multiple dates
const generateShiftInstances = (shiftData) => {
  const { dates, startTime, plannedDuration = 8, ...baseData } = shiftData;
  const instances = [];
  
  dates.forEach(date => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDateTime = new Date(date);
    startDateTime.setHours(hours, minutes, 0, 0);
    
    const endDateTime = new Date(startDateTime);
    endDateTime.setHours(startDateTime.getHours() + plannedDuration);
    
    // Check if shift goes over midnight
    const isNextDay = endDateTime.getDate() !== startDateTime.getDate();
    const endDate = isNextDay ? 
      new Date(startDateTime.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0] : 
      date;
    
    instances.push({
      ...baseData,
      date,
      startTime,
      plannedDuration,
      startDateTime: startDateTime.toISOString(),
      endDateTime: endDateTime.toISOString(),
      endDate, // Add endDate field for shifts that go over midnight
      isOvernightShift: isNextDay
    });
  });
  
  return instances;
};

// Async thunks
export const fetchPlannedShifts = createAsyncThunk(
  'plannedShifts/fetchPlannedShifts',
  async ({ startDate, endDate, driverId } = {}, { rejectWithValue }) => {
    try {
      let plannedShiftsQuery = collection(db, 'plannedShifts');
      
      // Build query based on parameters
      const queryConstraints = [];
      
      if (driverId) {
        queryConstraints.push(where('driverId', '==', driverId));
      }
      
      if (startDate || endDate) {
        queryConstraints.push(where('status', '==', 'active'));
      }
      
      queryConstraints.push(orderBy('createdAt', 'desc'));
      
      if (queryConstraints.length > 0) {
        plannedShiftsQuery = query(plannedShiftsQuery, ...queryConstraints);
      }
      
      const querySnapshot = await getDocs(plannedShiftsQuery);

      // Erst filtern, dann Fahrer/Fahrzeug-Daten parallel laden
      const filteredDocs = [];

      for (const shiftDoc of querySnapshot.docs) {
        const shiftData = { ...shiftDoc.data() };

        // Filter by date range if provided
        if (startDate || endDate) {
          const relevantDates = shiftData.dates.filter(date => {
            const shiftDate = new Date(date);
            if (startDate && shiftDate < new Date(startDate)) return false;
            if (endDate && shiftDate > new Date(endDate)) return false;
            return true;
          });

          // Also check if shift ends on the requested date (for overnight shifts)
          let includeShift = relevantDates.length > 0;

          if (!includeShift && shiftData.isOvernightShift) {
            // Check using dateEndMapping for multiple dates
            if (shiftData.dateEndMapping) {
              Object.values(shiftData.dateEndMapping).forEach(endDateStr => {
                const shiftEndDate = new Date(endDateStr);
                if (startDate && shiftEndDate >= new Date(startDate)) {
                  includeShift = true;
                }
                if (endDate && shiftEndDate <= new Date(endDate)) {
                  includeShift = true;
                }
              });
            }
            // Fallback to single endDate for backward compatibility
            else if (shiftData.endDate) {
              const shiftEndDate = new Date(shiftData.endDate);
              if (startDate && shiftEndDate >= new Date(startDate)) {
                includeShift = true;
              }
              if (endDate && shiftEndDate <= new Date(endDate)) {
                includeShift = true;
              }
            }
          }

          if (!includeShift) continue;

          // Include only relevant dates
          shiftData.dates = relevantDates;
        }

        filteredDocs.push({ id: shiftDoc.id, data: shiftData });
      }

      // Alle Fahrer/Fahrzeug-Daten parallel laden statt sequentiell
      const plannedShifts = await Promise.all(
        filteredDocs.map(async ({ id, data: shiftData }) => {
          const [driverResult, vehicleResult] = await Promise.all([
            shiftData.driverId
              ? getDoc(doc(db, 'users', shiftData.driverId))
              : Promise.resolve(null),
            shiftData.vehicleId
              ? getDoc(doc(db, 'Vehicles', shiftData.vehicleId))
              : Promise.resolve(null)
          ]);

          const driverData = driverResult?.exists?.() && driverResult.data().isDriver
            ? driverResult.data() : null;
          const vehicleData = vehicleResult?.exists?.()
            ? vehicleResult.data() : null;

          return {
            id,
            ...shiftData,
            driver: driverData,
            vehicle: vehicleData
          };
        })
      );

      return plannedShifts;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPlannedShiftById = createAsyncThunk(
  'plannedShifts/fetchPlannedShiftById',
  async (shiftId, { rejectWithValue }) => {
    try {
      const shiftDoc = await getDoc(doc(db, 'plannedShifts', shiftId));
      
      if (!shiftDoc.exists()) {
        throw new Error('Schicht nicht gefunden');
      }
      
      const shiftData = shiftDoc.data();
      
      // Fetch driver data
      let driverData = null;
      if (shiftData.driverId) {
        const userDoc = await getDoc(doc(db, 'users', shiftData.driverId));
        if (userDoc.exists() && userDoc.data().isDriver) {
          driverData = userDoc.data();
        }
      }
      
      // Fetch vehicle data if assigned
      let vehicleData = null;
      if (shiftData.vehicleId) {
        const vehicleDoc = await getDoc(doc(db, 'Vehicles', shiftData.vehicleId));
        if (vehicleDoc.exists()) {
          vehicleData = vehicleDoc.data();
        }
      }
      
      return {
        id: shiftDoc.id,
        ...shiftData,
        driver: driverData,
        vehicle: vehicleData
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createPlannedShifts = createAsyncThunk(
  'plannedShifts/createPlannedShifts',
  async (shiftData, { rejectWithValue, getState }) => {
    try {
      const { driverId, vehicleId, dates, startTime, plannedDuration = 8, notes } = shiftData;
      
      // Validate driver exists
      const userDoc = await getDoc(doc(db, 'users', driverId));
      if (!userDoc.exists() || !userDoc.data().isDriver) {
        throw new Error('Fahrer nicht gefunden');
      }
      const driverData = userDoc.data();
      
      // Validate vehicle if provided
      let vehicleData = null;
      if (vehicleId) {
        const vehicleDoc = await getDoc(doc(db, 'Vehicles', vehicleId));
        if (!vehicleDoc.exists()) {
          throw new Error('Fahrzeug nicht gefunden');
        }
        vehicleData = vehicleDoc.data();
      }
      
      // Check for conflicts with existing planned shifts
      const existingShiftsQuery = query(
        collection(db, 'plannedShifts'),
        where('driverId', '==', driverId),
        where('status', '==', 'active')
      );
      
      const existingShifts = await getDocs(existingShiftsQuery);
      const conflicts = [];
      
      for (const existingDoc of existingShifts.docs) {
        const existing = existingDoc.data();
        const conflictingDates = dates.filter(date => 
          existing.dates.includes(date) && existing.startTime === startTime
        );
        
        if (conflictingDates.length > 0) {
          conflicts.push({
            dates: conflictingDates,
            existingShiftId: existingDoc.id
          });
        }
      }
      
      if (conflicts.length > 0) {
        throw new Error(`Fahrer hat bereits Schichten an folgenden Tagen: ${conflicts[0].dates.join(', ')}`);
      }
      
      // Generate shift instances to get proper overnight shift data
      const shiftInstances = generateShiftInstances({
        driverId,
        driverName: driverData.name,
        vehicleId: vehicleId || null,
        vehiclePlate: vehicleData?.plate || null,
        dates: dates.sort(),
        startTime,
        plannedDuration,
        status: 'active',
        createdAt: new Date().toISOString(),
        createdBy: getState().auth.currentUser?.uid || 'system',
        notes: notes || ''
      });
      
      // For multiple dates, we need to handle overnight shifts properly
      // Create a mapping of date -> endDate for each day
      const dateEndMapping = {};
      const hasAnyOvernightShift = shiftInstances.some(instance => instance.isOvernightShift);
      
      shiftInstances.forEach(instance => {
        dateEndMapping[instance.date] = instance.endDate;
      });
      
      const newPlannedShift = {
        driverId,
        driverName: driverData.name,
        vehicleId: vehicleId || null,
        vehiclePlate: vehicleData?.plate || null,
        dates: dates.sort(),
        startTime,
        plannedDuration,
        status: 'active',
        createdAt: new Date().toISOString(),
        createdBy: getState().auth.currentUser?.uid || 'system',
        notes: notes || '',
        // Add overnight shift data
        isOvernightShift: hasAnyOvernightShift,
        dateEndMapping, // Map each start date to its end date
        // Keep backward compatibility with single endDate
        endDate: shiftInstances[0].endDate,
        startDateTime: shiftInstances[0].startDateTime,
        endDateTime: shiftInstances[0].endDateTime
      };
      
      const docRef = await addDoc(collection(db, 'plannedShifts'), newPlannedShift);
      
      return {
        id: docRef.id,
        ...newPlannedShift,
        driver: driverData,
        vehicle: vehicleData
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePlannedShift = createAsyncThunk(
  'plannedShifts/updatePlannedShift',
  async ({ id, ...updateData }, { rejectWithValue }) => {
    try {
      const shiftRef = doc(db, 'plannedShifts', id);
      
      // Check if shift exists
      const shiftDoc = await getDoc(shiftRef);
      if (!shiftDoc.exists()) {
        throw new Error('Geplante Schicht nicht gefunden');
      }
      
      // If updating dates or time, check for conflicts
      if (updateData.dates || updateData.startTime) {
        const currentData = shiftDoc.data();
        const newDates = updateData.dates || currentData.dates;
        const newStartTime = updateData.startTime || currentData.startTime;
        const driverId = updateData.driverId || currentData.driverId;
        
        // Check conflicts with other shifts
        const existingShiftsQuery = query(
          collection(db, 'plannedShifts'),
          where('driverId', '==', driverId),
          where('status', '==', 'active')
        );
        
        const existingShifts = await getDocs(existingShiftsQuery);
        const conflicts = [];
        
        for (const existingDoc of existingShifts.docs) {
          if (existingDoc.id === id) continue; // Skip current shift
          
          const existing = existingDoc.data();
          const conflictingDates = newDates.filter(date => 
            existing.dates.includes(date) && existing.startTime === newStartTime
          );
          
          if (conflictingDates.length > 0) {
            conflicts.push({
              dates: conflictingDates,
              existingShiftId: existingDoc.id
            });
          }
        }
        
        if (conflicts.length > 0) {
          throw new Error(`Fahrer hat bereits Schichten an folgenden Tagen: ${conflicts[0].dates.join(', ')}`);
        }
      }
      
      // Update the shift
      await updateDoc(shiftRef, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      
      // Fetch updated data
      const updatedDoc = await getDoc(shiftRef);
      const updatedData = updatedDoc.data();
      
      // Fetch related data
      let driverData = null;
      if (updatedData.driverId) {
        const userDoc = await getDoc(doc(db, 'users', updatedData.driverId));
        if (userDoc.exists()) {
          driverData = userDoc.data();
        }
      }
      
      let vehicleData = null;
      if (updatedData.vehicleId) {
        const vehicleDoc = await getDoc(doc(db, 'Vehicles', updatedData.vehicleId));
        if (vehicleDoc.exists()) {
          vehicleData = vehicleDoc.data();
        }
      }
      
      return {
        id,
        ...updatedData,
        driver: driverData,
        vehicle: vehicleData
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const cancelPlannedShift = createAsyncThunk(
  'plannedShifts/cancelPlannedShift',
  async ({ id, cancelDates = null }, { rejectWithValue }) => {
    try {
      const shiftRef = doc(db, 'plannedShifts', id);
      const shiftDoc = await getDoc(shiftRef);
      
      if (!shiftDoc.exists()) {
        throw new Error('Geplante Schicht nicht gefunden');
      }
      
      const currentData = shiftDoc.data();
      
      if (cancelDates && cancelDates.length > 0) {
        // Cancel only specific dates
        const remainingDates = currentData.dates.filter(date => !cancelDates.includes(date));
        
        if (remainingDates.length === 0) {
          // If no dates remain, cancel entire shift
          await updateDoc(shiftRef, {
            status: 'cancelled',
            cancelledAt: new Date().toISOString()
          });
        } else {
          // Update with remaining dates
          await updateDoc(shiftRef, {
            dates: remainingDates,
            updatedAt: new Date().toISOString()
          });
        }
      } else {
        // Cancel entire shift
        await updateDoc(shiftRef, {
          status: 'cancelled',
          cancelledAt: new Date().toISOString()
        });
      }
      
      // Return updated shift
      const updatedDoc = await getDoc(shiftRef);
      return {
        id,
        ...updatedDoc.data()
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deletePlannedShift = createAsyncThunk(
  'plannedShifts/deletePlannedShift',
  async (id, { rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, 'plannedShifts', id));
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch actual shifts from mobile app structure for comparison
export const fetchActualShiftsForComparison = createAsyncThunk(
  'plannedShifts/fetchActualShifts',
  async ({ date }, { rejectWithValue }) => {
    try {
      const shiftDoc = await getDoc(doc(db, 'shifts', date));
      
      if (!shiftDoc.exists()) {
        return { date, drivers: {} };
      }
      
      const data = shiftDoc.data();
      return {
        date,
        drivers: data.drivers || {}
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  plannedShifts: [],
  actualShifts: {}, // For comparison with mobile app data
  isLoading: false,
  error: null,
  currentPlannedShift: null
};

const plannedShiftsSlice = createSlice({
  name: 'plannedShifts',
  initialState,
  reducers: {
    clearPlannedShiftsError: (state) => {
      state.error = null;
    },
    clearCurrentPlannedShift: (state) => {
      state.currentPlannedShift = null;
    },
    setActualShifts: (state, action) => {
      state.actualShifts[action.payload.date] = action.payload.data;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch planned shifts
      .addCase(fetchPlannedShifts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlannedShifts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plannedShifts = action.payload;
      })
      .addCase(fetchPlannedShifts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch planned shift by ID
      .addCase(fetchPlannedShiftById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPlannedShiftById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPlannedShift = action.payload;
      })
      .addCase(fetchPlannedShiftById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create planned shifts
      .addCase(createPlannedShifts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createPlannedShifts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plannedShifts.push(action.payload);
        state.currentPlannedShift = action.payload;
      })
      .addCase(createPlannedShifts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update planned shift
      .addCase(updatePlannedShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePlannedShift.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.plannedShifts.findIndex(shift => shift.id === action.payload.id);
        if (index !== -1) {
          state.plannedShifts[index] = action.payload;
        }
        if (state.currentPlannedShift?.id === action.payload.id) {
          state.currentPlannedShift = action.payload;
        }
      })
      .addCase(updatePlannedShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Cancel planned shift
      .addCase(cancelPlannedShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelPlannedShift.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.plannedShifts.findIndex(shift => shift.id === action.payload.id);
        if (index !== -1) {
          state.plannedShifts[index] = action.payload;
        }
      })
      .addCase(cancelPlannedShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete planned shift
      .addCase(deletePlannedShift.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deletePlannedShift.fulfilled, (state, action) => {
        state.isLoading = false;
        state.plannedShifts = state.plannedShifts.filter(shift => shift.id !== action.payload);
        if (state.currentPlannedShift?.id === action.payload) {
          state.currentPlannedShift = null;
        }
      })
      .addCase(deletePlannedShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch actual shifts
      .addCase(fetchActualShiftsForComparison.fulfilled, (state, action) => {
        state.actualShifts[action.payload.date] = action.payload.drivers;
      });
  }
});

export const { 
  clearPlannedShiftsError, 
  clearCurrentPlannedShift,
  setActualShifts 
} = plannedShiftsSlice.actions;

export default plannedShiftsSlice.reducer;